import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, MessageCircle, Sparkles } from "lucide-react";
import { whatsappHref } from "@/lib/api";

// Template padrão CEAP SEM o nome (aplicamos o nome do cliente diretamente sobre o fundo transparente do card)
const TEMPLATE_URL =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_dashboard-ceap/artifacts/ajmg8ot4_Azul%20e%20Branco%20Moderno%20Anivers%C3%A1rio%20Post%20para%20Instagram%20%282%29.png";

/**
 * Cartão de Aniversário — usa o template padrão do CEAP como fundo.
 * A ÚNICA alteração é o nome onde está "MARIANA", com fonte Pluma 40.5px cor #1d7495.
 * Template 1080x1350. Posição do nome ajustada visualmente pra bater com o template.
 */
export default function CartaoAniversarioModal({ open, onClose, pessoa }) {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [imgUrl, setImgUrl] = useState(null);

  useEffect(() => {
    if (!open || !pessoa) return;
    setReady(false);
    setImgUrl(null);

    // Carrega Pluma se disponível localmente; fallback para stack de fontes similares (o CSS já importa Dancing Script)
    const drawCard = async () => {
      // aguarda o dialog portal montar canvas
      await new Promise((r) => setTimeout(r, 80));
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      canvas.width = 1080;
      canvas.height = 1350;

      // Carrega template via fetch+blob (evita CORS taint no canvas.toBlob)
      let templW = 1080, templH = 1350;
      try {
        const resp = await fetch(TEMPLATE_URL);
        const blob = await resp.blob();
        const bmpUrl = URL.createObjectURL(blob);
        const img = await new Promise((res, rej) => {
          const im = new Image();
          im.onload = () => res(im);
          im.onerror = rej;
          im.src = bmpUrl;
        });
        templW = img.naturalWidth || 1080;
        templH = img.naturalHeight || 1350;
        canvas.width = templW;
        canvas.height = templH;
        ctx.drawImage(img, 0, 0, templW, templH);
        URL.revokeObjectURL(bmpUrl);
      } catch {
        ctx.fillStyle = "#e6f0f7";
        ctx.fillRect(0, 0, templW, templH);
      }

      // Tenta garantir fontes carregadas (Dancing Script foi importado no index.html)
      try {
        if (document.fonts && document.fonts.load) {
          await document.fonts.load('bold italic 40.5px "Pluma"');
          await document.fonts.load('bold italic 40.5px "Dancing Script"');
        }
      } catch { /* ignora falhas de font loading */ }

      // Posição do nome do cliente sobre o template (onde estava "MARIANA")
      // Sem retângulo branco — o template novo já vem sem nome, fundo transparente do card
      const NAME_BASELINE_Y = Math.round(templH * 0.307);
      const NAME_X = Math.round(templW * 0.098);
      const MAX_W = Math.round(templW * 0.55);

      // Escreve nome do cliente em NEGRITO (Pluma / 40.5px proporcional / #1d7495)
      const fontSizePx = Math.round(40.5 * (templW / 1080));
      ctx.fillStyle = "#1d7495";
      ctx.font =
        `900 italic ${fontSizePx}px 'Pluma', 'Dancing Script', 'Kaushan Script', 'Pacifico', 'Segoe Script', 'Brush Script MT', cursive`;
      ctx.textAlign = "left";
      ctx.textBaseline = "alphabetic";
      const nome = (pessoa?.nome || "").trim().toUpperCase();
      let displayNome = nome;
      let px = fontSizePx;
      while (ctx.measureText(displayNome).width > MAX_W && px > 20) {
        px -= 1;
        ctx.font =
          `900 italic ${px}px 'Pluma', 'Dancing Script', 'Kaushan Script', 'Pacifico', 'Segoe Script', 'Brush Script MT', cursive`;
      }
      ctx.fillText(displayNome, NAME_X, NAME_BASELINE_Y);

      // Gera preview + blob
      canvas.toBlob(
        (blob) => {
          if (!blob) { setReady(true); return; }
          setBlobFile(blob);
          const url = URL.createObjectURL(blob);
          setImgUrl(url);
          setReady(true);
        },
        "image/png",
        0.95,
      );
    };

    drawCard();
  }, [open, pessoa?.id]);

  const [blobFile, setBlobFile] = useState(null);

  const baixar = () => {
    if (!imgUrl) return;
    const a = document.createElement("a");
    a.href = imgUrl;
    a.download = `parabens_${(pessoa?.nome || "cliente").replace(/\s+/g, "_")}.png`;
    a.click();
  };

  const compartilhar = async () => {
    if (!blobFile) return;
    try {
      const file = new File([blobFile], `parabens_${(pessoa?.nome || "cliente").replace(/\s+/g, "_")}.png`, { type: "image/png" });
      // Preferido: Web Share API (celular Android/iPhone anexa direto no WhatsApp escolhido)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Feliz aniversário!",
          text: `🎉 Feliz aniversário, ${pessoa?.nome || "amigo(a)"}! Com carinho, TIME CEAP BRASIL 💙`,
        });
        return;
      }
      // Fallback desktop: copia PNG pra área de transferência (usuário cola no WhatsApp Web)
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new ClipboardItem({ "image/png": blobFile })]);
        alert("Cartão copiado! Cole com Ctrl+V no chat do WhatsApp Web.");
        return;
      }
      // Último fallback: baixa
      baixar();
    } catch (err) {
      if (err?.name !== "AbortError") baixar();
    }
  };

  const abrirWa = whatsappHref(pessoa?.whatsapp || pessoa?.telefone);
  const msgWa = encodeURIComponent(
    `🎉 *Feliz aniversário, ${pessoa?.nome || "amigo(a)"}!* 🎂\n\nCom carinho,\nTIME CEAP BRASIL 💙`
  );
  const waComMsg = abrirWa ? `${abrirWa}&text=${msgWa}` : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="modal-cartao-aniversario">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#1d7495]" /> Cartão de Aniversário
          </DialogTitle>
          <DialogDescription>
            Cartão personalizado com <b>{pessoa?.nome}</b>. Faça download e envie pelo WhatsApp.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3">
          <canvas ref={canvasRef} className="hidden" />
          {imgUrl ? (
            <img
              src={imgUrl}
              alt="Cartão de aniversário"
              className="max-w-full rounded-xl border border-slate-200 shadow-lg"
              style={{ maxHeight: 520 }}
              data-testid="preview-cartao-aniv"
            />
          ) : (
            <div className="w-full h-96 flex items-center justify-center bg-slate-50 rounded-xl border border-dashed">
              <span className="text-sm text-slate-400 italic">Gerando cartão...</span>
            </div>
          )}
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={baixar}
            disabled={!ready}
            data-testid="btn-baixar-cartao"
          >
            <Download className="w-4 h-4 mr-1" /> Baixar PNG
          </Button>
          <Button
            onClick={compartilhar}
            disabled={!ready}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="btn-compartilhar-cartao"
            title="No celular anexa direto no WhatsApp. No PC, copia pra área de transferência."
          >
            <Sparkles className="w-4 h-4 mr-1" /> Enviar cartão (WhatsApp)
          </Button>
          {waComMsg && (
            <Button
              asChild
              variant="outline"
              className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
              data-testid="btn-abrir-wa-cartao"
              title="Abre o chat da pessoa apenas com a mensagem"
            >
              <a href={waComMsg} target="_blank" rel="noreferrer">
                <MessageCircle className="w-4 h-4 mr-1" /> Só a mensagem
              </a>
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} className="ml-auto">Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
